import React from "react";
import { resolveItemColor } from "@/lib/colorSwatch";

/**
 * Render an item's color, respecting Base44's new precedence (color field is
 * source of truth). Three visual variants:
 *
 *   • Canonical color:  colored dot + friendly name (e.g. ● White)
 *   • Custom paint:     verbatim text + amber "CUSTOM" pill
 *   • Empty:            SKU-inferred name or "—"
 *
 * Used inside line-item rows (order detail, label picker) so painters can
 * always tell at a glance whether they're looking at a stock finish or a
 * spec / drawing reference.
 */
export default function ItemColorLine({ item, className = "" }) {
  const info = resolveItemColor(item);
  const text = info.text || "—";

  return (
    <span
      className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}
      data-testid={`item-color-${item?.sku || "unknown"}`}
    >
      {info.hex ? (
        <span
          aria-hidden
          className="inline-block w-3 h-3 rounded-full border border-zinc-400 flex-shrink-0"
          style={{ background: info.hex }}
          data-testid="item-color-swatch"
        />
      ) : null}
      <span className="truncate min-w-0" title={info.text || undefined}>
        {text}
      </span>
      {info.isCustom && (
        <span
          className="inline-flex items-center rounded-md border border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700 px-1.5 py-0 text-[9px] font-bold uppercase tracking-[0.15em] whitespace-nowrap flex-shrink-0"
          data-testid="item-color-custom-pill"
          title="Custom paint / spec reference — not a stock finish"
        >
          Custom
        </span>
      )}
    </span>
  );
}
